/* Cltchworks — product.js
 * PDP behaviour: gallery, variant swatches, quantity, the topper add-on,
 * detail tabs, the review carousel and the sticky add bar.
 *
 * Everything degrades. Swatches are links to ?variant=…, the quantity is a real
 * number input, the sticky bar's button submits the main form via the HTML form
 * attribute, and the tabs render stacked when this file does not run.
 */
(function () {
  'use strict';

  function money(cents, template) {
    var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return template.replace(/[\d.,]+/, amount);
  }

  /* ---------------------------------------------------------------------
   * Gallery — thumbnails swap the main image.
   * ------------------------------------------------------------------- */
  function initGallery(root) {
    var main = root.querySelector('[data-gallery="main"]');
    var thumbs = root.querySelectorAll('.thumb');
    if (!main || !thumbs.length) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        thumbs.forEach(function (t) {
          t.classList.remove('on');
          t.removeAttribute('aria-current');
        });
        thumb.classList.add('on');
        thumb.setAttribute('aria-current', 'true');

        main.src = thumb.dataset.src;
        if (thumb.dataset.srcset) main.srcset = thumb.dataset.srcset;
        if (thumb.dataset.alt) main.alt = thumb.dataset.alt;

        // Bring a part-cut thumbnail fully into view once it is the selection.
        if (thumb.scrollIntoView) {
          thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        }
      });
    });

    initThumbStrip(root);
  }

  /* ---------------------------------------------------------------------
   * Thumbnail strip — paging arrows and mouse-drag scrolling.
   *
   * The strip has always been a horizontal scroller with its scrollbar
   * hidden, which is fine with a trackpad or a finger and gives a mouse no
   * way in at all: six of the HX-1's eight thumbnails fit, and the rest were
   * unreachable. The arrows appear only when the strip actually overflows,
   * and are disabled at each end, so a product with three images shows none.
   * ------------------------------------------------------------------- */
  function initThumbStrip(root) {
    var wraps = root.querySelectorAll('[data-thumbwrap]');

    wraps.forEach(function (wrap) {
      var track = wrap.querySelector('[data-thumbs]');
      var prev = wrap.querySelector('[data-thumbnav="prev"]');
      var next = wrap.querySelector('[data-thumbnav="next"]');
      if (!track || !prev || !next) return;

      // A thumbnail plus its gap, so a page lands on a boundary.
      function step() {
        var one = track.querySelector('.thumb');
        var w = one ? one.getBoundingClientRect().width + 10 : 98;
        return Math.max(w, Math.floor(track.clientWidth / w) * w);
      }

      /* Wider than the buttons themselves. A strip hiding its last 7px — which
         is what the HX-1 does on a tablet — would otherwise put a 34px button
         over the content to reveal seven pixels of it. */
      var MIN_OVERFLOW = 28;

      function sync() {
        var max = track.scrollWidth - track.clientWidth;
        var overflows = max > MIN_OVERFLOW;
        prev.hidden = !overflows || track.scrollLeft <= 1;
        next.hidden = !overflows || track.scrollLeft >= max - 1;
        track.classList.toggle('is-draggable', overflows);
      }

      prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      });
      next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      });

      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      // Thumbnails are lazy-loaded, so scrollWidth is not final at parse time.
      if (window.ResizeObserver) new ResizeObserver(sync).observe(track);
      track.querySelectorAll('img').forEach(function (img) {
        if (!img.complete) img.addEventListener('load', sync, { once: true });
      });
      sync();

      /* Mouse drag. Touch and trackpad already scroll natively and do it
         better, so this only takes over for a mouse — otherwise it would
         fight momentum scrolling on a phone. */
      var down = false, moved = false, startX = 0, startLeft = 0;

      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        if (track.scrollWidth - track.clientWidth <= MIN_OVERFLOW) return;
        down = true;
        moved = false;
        startX = e.clientX;
        startLeft = track.scrollLeft;
      });

      track.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        // A few pixels of slop, so a slightly shaky click is still a click.
        if (!moved && Math.abs(dx) < 4) return;
        if (!moved) {
          moved = true;
          track.classList.add('is-dragging');
          if (track.setPointerCapture) track.setPointerCapture(e.pointerId);
        }
        e.preventDefault();
        track.scrollLeft = startLeft - dx;
      });

      function endDrag(e) {
        if (!down) return;
        down = false;
        if (moved && track.releasePointerCapture && e.pointerId != null) {
          try { track.releasePointerCapture(e.pointerId); } catch (err) {}
        }
        // Next frame: the class also gates .thumb pointer-events, and dropping
        // it in the same tick lets the click through to the thumbnail.
        requestAnimationFrame(function () { track.classList.remove('is-dragging'); });
        sync();
      }

      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      // Dragging an image is the browser's default and it hijacks the gesture.
      track.addEventListener('dragstart', function (e) { e.preventDefault(); });
    });
  }

  /* ---------------------------------------------------------------------
   * Variants — swatch/pill links swap the selection without a page load.
   * ------------------------------------------------------------------- */
  function initVariants(root) {
    var dataEl = root.querySelector('[data-product-variants]');
    var idField = root.querySelector('[data-product="variant-id"]');
    if (!dataEl || !idField) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!variants || !variants.length) return;

    var priceEl = root.querySelector('[data-product="price"]');
    var submitPrices = root.querySelectorAll('[data-product="submit-price"]');
    /* Both the buy row and the sticky bar. The sticky one used to be missed, so
       a sold-out colourway disabled the button you were looking at and left the
       one at the bottom of the screen live. */
    var submits = root.querySelectorAll('[data-product="submit"]');
    var addLabels = root.querySelectorAll('[data-product="add-label"]');
    var soldLabels = root.querySelectorAll('[data-product="sold-label"]');
    var stickySub = root.querySelector('[data-sticky="subtitle"]');
    var options = root.querySelectorAll('[data-option-value]');
    if (!options.length) return;

    /* Stock per variant, from its own payload: Liquid's variants JSON drops
       inventory entirely, so the count cannot be read off `variants` above.
       A zero here means "say nothing" — either the variant has plenty, is not
       tracked, or is allowed to sell past zero — not that it is sold out. */
    var lowStock = root.querySelector('[data-product="low-stock"]');
    var lowStockCount = root.querySelector('[data-product="low-stock-count"]');
    var stockBefore = root.querySelector('[data-product="stock-before"]');
    var stockAfter = root.querySelector('[data-product="stock-after"]');
    var stock = {};
    var stockEl = root.querySelector('[data-product-stock]');
    if (stockEl) {
      try {
        stock = JSON.parse(stockEl.textContent) || {};
      } catch (e) {
        stock = {};
      }
    }

    /* The money format is read off the rendered price, so currency and
       Markets formatting are whatever Liquid already produced. */
    var priceTemplate = priceEl ? priceEl.textContent.trim() : '';

    function apply(variant) {
      idField.value = variant.id;

      if (priceEl && priceTemplate) priceEl.textContent = money(variant.price, priceTemplate);
      submitPrices.forEach(function (el) {
        if (priceTemplate) el.textContent = money(variant.price, priceTemplate);
      });

      submits.forEach(function (el) { el.disabled = !variant.available; });
      addLabels.forEach(function (el) { el.hidden = !variant.available; });
      soldLabels.forEach(function (el) { el.hidden = variant.available; });

      if (lowStock) {
        var left = Number(stock[variant.id]) || 0;
        /* Hidden for a sold-out variant too: the button already says so, and
           two messages about the same fact is one too many. */
        var showStock = left > 0 && variant.available;
        lowStock.hidden = !showStock;

        if (showStock) {
          /* Which register this variant belongs in. Both wordings came down on
             the element, so switching between "Only 3 left" and "22 in stock"
             needs no round trip. */
          var lowMax = Number(lowStock.getAttribute('data-low-max')) || 0;
          var isLow = left <= lowMax;
          var copy = isLow
            ? lowStock.getAttribute('data-low-text')
            : lowStock.getAttribute('data-plenty-text');

          lowStock.classList.toggle('lowstock--plenty', !isLow);

          if (lowStockCount) lowStockCount.textContent = String(left);
          if (stockBefore && stockAfter && copy != null) {
            var split = copy.split('[n]');
            stockBefore.textContent = split[0] || '';
            stockAfter.textContent = split.length > 1 ? split[1] : '';
            if (lowStockCount) lowStockCount.hidden = split.length < 2;
          }
        }
      }

      options.forEach(function (el) {
        var index = Number(el.dataset.optionIndex);
        var isOn = variant.options[index] === el.dataset.optionValue;
        el.classList.toggle('on', isOn);
        if (isOn) {
          el.setAttribute('aria-current', 'true');
          var label = root.querySelector('[data-option-selected="' + index + '"]');
          if (label) label.textContent = el.dataset.optionValue;
        } else {
          el.removeAttribute('aria-current');
        }
      });

      if (stickySub) {
        var parts = stickySub.textContent.split('·');
        stickySub.textContent = (parts[0] || '').trim() + ' · ' + variant.title;
      }

      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    options.forEach(function (el) {
      el.addEventListener('click', function (event) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        var id = Number(el.dataset.variant);
        var variant = variants.filter(function (v) { return v.id === id; })[0];
        if (!variant) return;
        event.preventDefault();
        apply(variant);
      });
    });
  }

  /* ---------------------------------------------------------------------
   * Quantity stepper around the native number input.
   * ------------------------------------------------------------------- */
  function initQuantity(root) {
    var input = root.querySelector('[data-qty="input"]');
    if (!input) return;

    function step(by) {
      var next = (parseInt(input.value, 10) || 1) + by;
      input.value = Math.max(1, next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    var down = root.querySelector('[data-qty="down"]');
    var up = root.querySelector('[data-qty="up"]');
    if (down) down.addEventListener('click', function () { step(-1); });
    if (up) up.addEventListener('click', function () { step(1); });
  }

  /* ---------------------------------------------------------------------
   * Add-on — enables a second line item on the same cart post.
   *
   * The hidden inputs start disabled so an unchecked box submits nothing at
   * all; a half-populated items[1] would be rejected by /cart/add.
   * ------------------------------------------------------------------- */
  function initAddon(root) {
    var toggle = root.querySelector('[data-addon-toggle]');
    var idField = root.querySelector('[data-addon-id]');
    var qtyField = root.querySelector('[data-addon-qty]');
    if (!toggle || !idField || !qtyField) return;

    toggle.addEventListener('change', function () {
      var on = toggle.checked;
      idField.value = on ? idField.dataset.addonVariant : '';
      idField.disabled = !on;
      qtyField.disabled = !on;
    });
  }

  /* ---------------------------------------------------------------------
   * Detail tabs — ARIA tablist with arrow-key support.
   * ------------------------------------------------------------------- */
  function initTabs(root) {
    root.querySelectorAll('[data-tabs]').forEach(function (wrap) {
      var tabs = Array.prototype.slice.call(wrap.querySelectorAll('[role="tab"]'));
      var panels = Array.prototype.slice.call(wrap.querySelectorAll('[role="tabpanel"]'));
      if (tabs.length < 2) return;

      function select(index) {
        tabs.forEach(function (tab, i) {
          var on = i === index;
          tab.classList.toggle('on', on);
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.tabIndex = on ? 0 : -1;
        });
        panels.forEach(function (panel, i) {
          var on = i === index;
          panel.classList.toggle('on', on);
          panel.hidden = !on;
        });
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { select(i); });
        tab.addEventListener('keydown', function (event) {
          var next = null;
          if (event.key === 'ArrowRight') next = (i + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next === null) return;
          event.preventDefault();
          select(next);
          tabs[next].focus();
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
   * Reviews carousel.
   * ------------------------------------------------------------------- */
  function initReviews(root) {
    root.querySelectorAll('[data-rev]').forEach(function (wrap) {
      var track = wrap.querySelector('.revtrack');
      var prev = wrap.querySelector('.rev-prev');
      var next = wrap.querySelector('.rev-next');
      if (!track || !prev || !next) return;

      var step = function () { return Math.min(track.clientWidth * 0.9, 440); };
      prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
    });
  }

  /* ---------------------------------------------------------------------
   * Sticky add bar — appears once the main button scrolls out of view.
   * ------------------------------------------------------------------- */
  function initStickyBar(root) {
    var bar = root.querySelector('[data-sticky-bar]');
    /* The bar shows once the buy row's own button leaves the screen, so this
       has to be that button and not the bar's copy of it — which now also
       carries data-product="submit" so it gets disabled with the other. The
       buy row's is the one inside the product form. */
    var anchor = root.querySelector('.product-form [data-product="submit"]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('show', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      { threshold: 0 }
    ).observe(anchor);
  }

  function init(root) {
    var scope = root || document;
    initGallery(scope);
    initVariants(scope);
    initQuantity(scope);
    initAddon(scope);
    initTabs(scope);
    initReviews(scope);
    initStickyBar(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
